import type { ReportFilterOption, ReportOptionFilterKey } from '@chukua/common';
import { Select } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useRemoteSearchOptions } from '../../../shared/hooks/use-remote-search-options';
import { labelFilterOptions, searchFilterOptions } from '../reports.api';
import { FILTER_LABELS } from '../policies/visible-filters.policy';

interface ReportFilterSelectProps {
  filter: ReportOptionFilterKey;
  values: string[];
  onChange: (values: string[]) => void;
}

/** One picker of the filter bar. It opens on the server's first choices; the values already chosen are labelled by name, not id. */
export function ReportFilterSelect({ filter, values, onChange }: ReportFilterSelectProps) {
  const fetchOptions = useCallback((q: string | undefined) => searchFilterOptions(filter, q), [filter]);
  const { selectProps } = useRemoteSearchOptions(fetchOptions);
  const [chosen, setChosen] = useState<ReportFilterOption[]>([]);
  const valuesKey = values.join(',');

  useEffect(() => {
    if (!valuesKey) return;
    void labelFilterOptions(filter, valuesKey.split(','))
      .then(setChosen)
      .catch(() => undefined);
  }, [filter, valuesKey]);

  const listed = new Set(selectProps.options.map((option) => option.value));
  const options = [...chosen.filter((option) => values.includes(option.value) && !listed.has(option.value)), ...selectProps.options];

  return (
    <Select
      {...selectProps}
      options={options}
      mode="multiple"
      allowClear
      maxTagCount="responsive"
      aria-label={FILTER_LABELS[filter]}
      placeholder={FILTER_LABELS[filter]}
      className="w-full min-w-40 sm:w-56"
      value={values}
      onChange={onChange}
    />
  );
}
